import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Branches } from './pages/Branches';
import { Hospitals } from './pages/Hospitals';
import { Users } from './pages/Users';
import { BloodReference } from './pages/BloodReference';
import { Pricing } from './pages/Pricing';
import { Stock } from './pages/Stock';
import { Donations } from './pages/Donations';
import { Donors } from './pages/Donors';
import { Events } from './pages/Events';
import { FormBuilder } from './pages/FormBuilder';
import { Orders } from './pages/Orders';
import { Payments } from './pages/Payments';
import { Ledger } from './pages/Ledger';
import { Settings } from './pages/Settings';
import { ChangePassword } from './pages/ChangePassword';
import { ServiceRequests } from './pages/ServiceRequests';
import { ServiceTypes } from './pages/ServiceTypes';
import { ServiceDashboard } from './pages/ServiceDashboard';
import { MicrositeApp } from './microsite/MicrositeApp';

type Perm = Parameters<ReturnType<typeof useAuth>['can']>[0];

/** The landing page differs per role: hospital users cannot see the branch list. */
function homeFor(can: (p: Perm) => boolean): string {
  return can('order:create') ? '/orders'
    // Hospital finance can neither order nor read stock; payments is its whole job.
    : can('payment:create') ? '/payments'
    : can('stock:read') ? '/stock'
    : can('branch:read') ? '/branches'
    // A services admin has nothing but the Layanan menu.
    : can('service_request:read') ? '/service-requests'
    : '/orders';
}

/** Routes gated on a permission; without it, redirect to a page the user may see. */
function Guard({ permission, children }: { permission: Perm; children: React.ReactNode }) {
  const { can } = useAuth();
  // Home, not a fixed page: redirecting a services admin to /orders would bounce it
  // straight into another guard it cannot pass.
  return can(permission) ? <>{children}</> : <Navigate to={homeFor(can)} replace />;
}

function Shell() {
  const { user, loading, can } = useAuth();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <p className="muted">Memuat sesi…</p>
      </div>
    );
  }

  if (!user) return <Login />;

  const home = homeFor(can);

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/branches" element={<Guard permission="branch:read"><Branches /></Guard>} />
        <Route path="/hospitals" element={<Guard permission="hospital:read"><Hospitals /></Guard>} />
        <Route path="/orders" element={<Guard permission="order:read"><Orders /></Guard>} />
        <Route path="/payments" element={<Guard permission="payment:read"><Payments /></Guard>} />
        <Route path="/ledger" element={<Guard permission="ledger:read"><Ledger /></Guard>} />
        <Route path="/stock" element={<Guard permission="stock:read"><Stock /></Guard>} />
        <Route path="/donations" element={<Guard permission="donation:read"><Donations /></Guard>} />
        <Route path="/donors" element={<Guard permission="donor:read"><Donors /></Guard>} />
        <Route path="/events" element={<Guard permission="event:read"><Events /></Guard>} />
        <Route path="/forms" element={<Guard permission="form:read"><FormBuilder purpose="donor" /></Guard>} />
        <Route path="/service-forms" element={<Guard permission="service_form:read"><FormBuilder key="service" purpose="service" /></Guard>} />
        <Route path="/service-requests" element={<Guard permission="service_request:read"><ServiceRequests /></Guard>} />
        <Route path="/service-types" element={<Guard permission="service:read"><ServiceTypes /></Guard>} />
        <Route path="/service-dashboard" element={<Guard permission="service_request:read"><ServiceDashboard /></Guard>} />
        <Route path="/blood-reference" element={<Guard permission="blood:read"><BloodReference /></Guard>} />
        <Route path="/pricing" element={<Guard permission="price:read"><Pricing /></Guard>} />
        <Route path="/users" element={<Guard permission="user:read"><Users /></Guard>} />
        <Route path="/settings" element={<Guard permission="config:read"><Settings /></Guard>} />
        {/* Own-account action: ungated, since every signed-in user may change their own password. */}
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/*
          The donor microsite sits outside AuthProvider on purpose. A donor arrives with an
          AiYO SSO token and no backoffice account, and the admin shell renders the login
          screen whenever there is no admin session — which would make the microsite
          unreachable.
        */}
        <Route path="/microsite/*" element={<MicrositeApp />} />
        <Route
          path="/*"
          element={<AuthProvider><Shell /></AuthProvider>}
        />
      </Routes>
    </BrowserRouter>
  );
}
